import React, { useRef, useState } from "react";

import { ImagePreviewModal } from "./ImagePreviewModal";

/** 이미지 선택·삭제와 선택적 확대 미리보기를 제공하는 공통 입력 컴포넌트입니다. */
interface Props {
	/** 이미지 박스 제목 */
	title: string;
	/** data URL 접두사를 제외한 Base64 이미지 문자열 */
	image: string;
	/** 선택 또는 삭제로 이미지 값이 변경될 때 호출 */
	onImageChange: (base64Image: string) => void;
	/** true인 화면에서만 이미지 클릭 확대를 활성화 */
	previewEnabled?: boolean;
	/** 확대 팝업에서 title 대신 사용할 제목 */
	previewTitle?: string;
	/** 화면별 이미지 박스 크기와 배치를 지정하는 Tailwind 클래스 */
	wrapperOption?: {
		wrapperStyle: string;
	};
}

export const ImageForm = (props: Props) => {

	const { 
		title,
		image,
		onImageChange,
		previewEnabled = false,
		previewTitle,
		wrapperOption = {
			wrapperStyle: "w-[120px] h-[120px]",
		}
	} = props;
	// 실제 파일 선택 input은 숨기고 선택 버튼으로 클릭합니다.
	const fileInputRef = useRef<HTMLInputElement>(null);
	// 확대 팝업 표시 여부는 각 이미지 박스가 독립적으로 관리합니다.
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);

	const handleSelect = () => {
		fileInputRef.current?.click();
	};

	const handleDelete = () => {
		onImageChange(""); // 이미지 제거
	};

	// 선택한 파일을 Base64로 읽고 기존 DTO 계약에 맞춰 data URL 접두사를 제거합니다.
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // 1. 사용자가 선택한 File 객체를 가져옵니다.
        const file = e.target.files?.[0];
		if (!file) return;

        // 2. 파일을 읽을 FileReader를 생성합니다.
        const reader = new FileReader();

        // 4. 3번의 파일 읽기와 Data URL 생성이 완료되면 실행됩니다.
        reader.onloadend = () => {
            // result:
            // "data:image/png;base64,iVBORw0KGgo..."
            const result = reader.result as string;

            // 5. Data URL 접두어를 제거합니다.
            // "data:image/png;base64,iVBOR..."
            //                     ↓
            // "iVBOR..."
			const base64 = result.replace(/^data:image\/[a-z]+;base64,/, "");

            // 6. 순수 Base64 문자열을 부모에게 전달합니다.
            onImageChange(base64);
		};
        // 3.실제 파일 읽기와 Base64 인코딩이 시작되는 부분입니다.
        reader.readAsDataURL(file);
	};


	return <div className={`
								${wrapperOption.wrapperStyle}
								border border-gray-300 flex flex-col items-center`
							}>
		<div className="w-full h-[24px] border-b border-gray-300 flex justify-center items-center">
			<p tabIndex={0}>
				{title}
			</p>
		</div>
		{/* 확대 기능을 활성화한 화면에서는 이미지 영역을 클릭 가능한 버튼으로 사용합니다. */}
		{previewEnabled ? (
			<button
				type="button"
				className="w-full flex-1 min-h-0 overflow-hidden flex items-center justify-center disabled:cursor-default enabled:cursor-zoom-in"
				onClick={() => setIsPreviewOpen(true)}
				disabled={!image}
				aria-label={`${previewTitle ?? title} 확대 보기`}
			>
				{image && (
					<img
						className="max-w-full max-h-full object-contain"
						src={`data:image/png;base64,${image}`}
						alt={title}
					/>
				)}
			</button>
		) : (
			<div className="w-full flex-1 min-h-0 overflow-hidden flex items-center justify-center">
				{image && (
					<img
						className="max-w-full max-h-full object-contain"
						src={`data:image/png;base64,${image}`}
						alt={title}
					/>
				)}
			</div>
		)}
		<div className="w-full h-[24px] flex justify-center items-center gap-[6px] border-t border-gray-300">
				<button
					className="text-white h-[80%] rounded-[4px] bg-blue-dark px-[8px] text-[12px] font-semibold flex items-center"
					onClick={handleSelect}
				>
					선택
				</button>
				<button
					className="text-white h-[80%] rounded-[4px] bg-red-light px-[8px] text-[12px] font-semibold flex items-center"
					onClick={handleDelete}
				>
					삭제
				</button>
			</div>
		<input
			type="file"
			accept="image/*"
			ref={fileInputRef}
			style={{ display: "none" }}
			onChange={handleFileChange}
		/>
		{/* 팝업은 previewEnabled가 true이고 이미지가 있을 때만 실제 화면에 렌더링됩니다. */}
		<ImagePreviewModal
			isOpen={isPreviewOpen}
			title={previewTitle ?? title}
			image={image}
			onClose={() => setIsPreviewOpen(false)}
		/>
	</div>
}
